import React, { useState, useEffect, useMemo, CSSProperties } from "react";
import {
  DataGridPro,
  useGridApiRef,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro"
import { 
  FormControlLabel,
  RadioGroup, 
  Radio, 
  FormControl,  
  SelectChangeEvent} from "@mui/material";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import { IMessageBoxParams } from "../../../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../../../common/ConfirmDialog";
import {
  AppConfig,
  ProjectStatus,
  SettingType,
  ConfigurationFileType,
  ChartDialogConfig,
} from "../../../../../common/constants";
import { AnyType, WebAPI } from "../../../../../webapi/constants";
import AuthService from "../../../../../common/AuthService";
import BackButton from "../../../common/BackButton";
import ReloadButton from "../../../../common/ReloadButton";
import UploadSettingFileButton from "../file/UploadSettingFileButton";
import ResetFileButton from "../file/ResetFileButton";
import DownloadSFileButton from "../file/DownloadConfFileButton";
import { getProjects, IProjectRow } from "../../../../../webapi/service/getProjects";

import "../../../../main.css";
import "../../../../../App.css";
import { Button, FormLabel } from "react-bootstrap";
import { IProtData } from "../../../../../webapi/service/getFIle";
import { getModelEvaluationLists, IModelEvaluationListsRow } from "../../../../../webapi/service/getModelEvaluationLists";
import { evaluateModel } from "../../../../../webapi/service/evaluateModel";
import ModelDownloadButton from "./ModelDownloadButton";
import { downloadFkdppModel } from "../../../../../webapi/service/downloadFkdppModel";
import { deleteConfFile } from "../../../../../webapi/service/deleteConfFile";
import { uploadConfFile } from "../../../../../webapi/service/uploadConfFile";
import { downloadConfFile } from "../../../../../webapi/service/downloadConfFile";
import { GetModelEvaluationData, IModelEvaluationDataRow } from "../../../../../webapi/service/getModelEvaluationData";
import { getModelTrainConfig } from "../../../../../webapi/service/getModelTrainConfig";
import TrimmingChartDialog from "../file/TrimmingChartDialog";
import { getConfFile } from "../../../../../webapi/service/getConfFile";


export interface IProps {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * 戻るボタンが押された場合に呼び出されます
   */
  onBack: () => void;
  /**
   * プロジェクト一覧画面に戻る場合に呼び出します
   */
  onTop: () => void;
  /**
   * ファイル一覧をロードする場合に呼び出します
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoadFiles: (loading: boolean) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (param: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

export default function FkdppEvaluation(props: IProps): JSX.Element {
  const StatusNone = ProjectStatus.NONE;
  const StatusDoing = ProjectStatus.DOING;
  const StatusDone = ProjectStatus.DONE;
  const Automode = SettingType.AUTO;
  const Custommode = SettingType.CUSTOM;
  const evalconf = ConfigurationFileType.VALIDATION_CONF;
  const dialog_area_correction = ChartDialogConfig.CHAERT_DIALOG_AREA_CORRECTION;
  const { i18n, t } = useTranslation();
  const apiRef = useGridApiRef();
  const { 
    authService, 
    projectInfo, 
    showMessage, 
    showConfirm, 
    onBack, 
    onTop, 
    onLoading, 
    onCommunicationError, 
    resetUnoperatedTimeout 
  } = props;
  const projectid = projectInfo.projectid;
  const [rows, setRows] = useState<IModelEvaluationListsRow[]>([]);
  const [fkdppevalsts, setFkdppevalsts] = useState<string>(projectInfo.fkdppevalsts);
  const [evalconffilename, setEvalconffilename] = useState<string>("");
  //アップロード中はtrue
  const [uploading, setUploading] = useState<boolean>(false);
  const [mode, setMode] = useState<string>(Automode);
  const handleChangeMode = (event: SelectChangeEvent) => {
    setMode(event.target.value as string);
  }
  const [trainConfig, setTrainconfig] = useState<string>("");
  const [showEvaluationDialog, setShowEvaluationDialog] = useState<boolean>(false);
  const [selectProt, setSelectProt] = useState<IProtData|undefined>(undefined);
  const [dialogInfo, setDialogInfo] = useState<IModelEvaluationDataRow>();
  const [width, setWidth] = useState<number>(window.innerWidth * dialog_area_correction);
  const [height, setHeight] = useState<number>(window.innerHeight * dialog_area_correction);

  const descNone = t("fkdpp_eval_statusnone");
  const descDoing = t("fkdpp_eval_statusdoing");
  const descDone = t("fkdpp_eval_statusdone");
  const label_auto = t("fkdpp_eval_automode");
  const label_custom = t("fkdpp_eval_custommode");

  useEffect(() => {
    loadEvaluations(true);
    loadStatus();
    loadDescription();
    loadTrainConfig();
    loadConffile();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setBtnMode();
  }, [trainConfig]);

  function handleResize() {
    const { innerWidth, innerHeight } = window;
      setWidth(innerWidth * dialog_area_correction);
      setHeight(innerHeight * dialog_area_correction);
  }

  function loadEvaluations(loading: boolean) { 
    onLoading(loading);
    getModelEvaluationLists({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        setRows(rows);
        setTimeout(() => {
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function loadStatus() {
    onLoading(true);
    resetUnoperatedTimeout();
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setFkdppevalsts(row.fkdppevalsts);
        setTimeout(() => {
          onLoading(false);
        });
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      },
    });
  }

  function loadTrainConfig() {
    getModelTrainConfig({
      authService: authService,
      projectid: projectid,
      success: (row) => {
        setTrainconfig(row.learnmode);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      }
    })
  }

  function loadConffile() {
    onLoading(true);
    getConfFile({
      authService: authService,
      projectid: projectid,
      type: evalconf,
      success: (row) => {
        setEvalconffilename(row.name);
        onLoading(false);
      }, 
      fail: (id, status) => {
        onCommunicationError(id, status);
      }
    })
  }

  function setBtnMode() {
    if (trainConfig === Custommode) {
      setMode(Custommode);
    }
  }

  function handleReload() {
    resetUnoperatedTimeout();
    loadEvaluations(true);
    loadStatus();
    loadDescription();
  }

  function handleEvaluationModel(mode: string) {
    resetUnoperatedTimeout();
    onLoading(true);
    getProjects({
      authService: authService,
      projectid: projectid,
      success: (rows) => {
        const row = rows[0];
        setFkdppevalsts(row.fkdppevalsts);
        setTimeout(() => {
          onLoading(false);
          if (row.fkdppevalsts === StatusDoing) {
            showMessage({
              title: t("fkdppevalsts_doing"),
              message: t("fkdppevalsts_doing_message"),
            });
          } else if (row.simcreatests === StatusDoing) {
            showMessage({
              title: t("simcreatests_doing"),
              message: t("simcreatests_doing_message"),
            });
          } else if (row.simevalsts === StatusDoing) {
            showMessage({
              title: t("simevalsts_doing"),
              message: t("simevalsts_doing_message"),
            });
          } else if (row.fkdpplearnsts === StatusDoing) {
            showMessage({
              title: t("fkdpplearnsts_doing"),
              message: t("fkdpplearnsts_doing_message"),
            });
          } else if (row.fkdppsimsts === StatusDoing) {
            showMessage({
              title: t("fkdppsimsts_doing"),
              message: t("fkdppsimsts_doing_message"),
            });
          } else if (row.fkdppevalsts === StatusDone) {
            showConfirm({
              title: t("fkdpp_eval_confirm"),
              message: t("fkdpp_eval_confirm_message"),
              onOk: () => startEvaluateModel(mode)
            });
          } else {
            startEvaluateModel(mode);
          }   
        });
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      },
    });
  }

  function startEvaluateModel(mode: string) {
    evaluateModel({
      authService: authService,
      projectid: projectid,
      mode: mode,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      }
    });
  }

  function handleDownload(modelid: string) {
    resetUnoperatedTimeout();
    downloadFkdppModel({
      authService: authService,
      projectid: projectid,
      modelid: modelid,
      filename: "model_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  function handleOpenEvaluationDialog(modelid: string) {
    resetUnoperatedTimeout();
    onLoading(true);
    GetModelEvaluationData({
      authService: authService,
      projectid: projectid,
      modelid: modelid,
      success: (data) => {
        const prot: IProtData = {
          fileid: modelid,
          fname: data.id.toString(),
          datanum: data.datanum,
          interval: data.interval,
          input: data.outrows,
          output: data.pvrows
        };
        setDialogInfo(data);
        setSelectProt(prot);
        setShowEvaluationDialog(true);
        onLoading(false);
      },
      fail: (id, status) => {
        onLoading(false);
        onCommunicationError(id, status);
      }
    });
  }

  function handleCloseEvaluationDialog() {
    setShowEvaluationDialog(false);
    setSelectProt(undefined);
  }

  const handleResetConfFile = (type: string) => {
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    deleteConfFile({
      authService: authService,
      projectid: projectid,
      type: type,
      success: () => {
        setUploading(false);
        onLoading(false);
        loadConffile();
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);
      },
    });
  }

  //設定ファイルアップロード
  const handleSelectConfFile = (
    event: React.ChangeEvent<HTMLInputElement>, 
    type: string
    ) => {
    const files = event.target.files;
    if(!files) {
      return;
    }
    if (files.length > AppConfig.MAX_SETTING_FILE) {
      onCommunicationError(WebAPI.ID_NUM_FILE_EXCEED, undefined);
      return;
    }
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > AppConfig.MAX_UPLOAD_FILE_SIZE) { 
        onCommunicationError(WebAPI.ID_FILE_TOO_LARGE, undefined);
        return;
      }
    }
    onLoading(true);
    resetUnoperatedTimeout();
    setUploading(true);
    const fileList: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      fileList.push(file);
    }
    uploadConfFile({
      authService: authService,
      projectid: projectid,
      files: fileList,
      type: type,
      success: () => {
        setUploading(false);
        onLoading(false);
        loadConffile();
      },
      fail: (id, status) => {
        setUploading(false);
        onCommunicationError(id, status);
      },
    });  
  }

  //設定ファイルダウンロード
  const handleDownloadConfFile = (type: string) => {
    resetUnoperatedTimeout();
    downloadConfFile({
      authService: authService,
      projectid: projectid,
      type: type,
      filename: "conffile_" + type + "_" + projectid,
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "modelid",
        headerName: t("model_list_id"),
        width: 150,
        hide: true,
      },
      {
        field: "name",
        headerName: t("model_list_name"),
        width: 150,
        sortable: true,
      },
      {
        field: "settlingtime",
        headerName: t("model_list_settlingtime"),
        width: 150,
        sortable: true,
      },
      {
        field: "risetime",
        headerName: t("model_list_risetime"),
        width: 180,
        sortable: true,
      },
      {
        field: "overshoot",
        headerName: t("model_list_overshoot"),
        width: 150,
        sortable: true,
      },
      {
        field: "offset",
        headerName: t("model_list_offset"),
        width: 150,
        sortable: true,
      },
      {
        field: "stability",
        headerName: t("model_list_stability"),
        width: 150,
        sortable: true,
      },
      {
        field: "actions",
        type: "actions",
        headerName: t("model_list_download"),
        width: 120,
        renderCell: (params: GridRenderCellParams<AnyType, IModelEvaluationListsRow>) => {
          const { row } = params;
          const dlBtn = <ModelDownloadButton onClick={() => handleDownload(row.modelid)}/>
          return dlBtn;
        },
      }
    ];
  }, [i18n.language]);

  const dataGrid = useMemo(() => {
    return (
      <DataGridPro 
        columns={columns}
        rows={rows}
        disableSelectionOnClick
        disableColumnMenu
        onCellClick={(params) => {
          if (params.field === "actions") {
            return;
          }
          const row = params.row;
          handleOpenEvaluationDialog(row.modelid);
        }}
        getRowClassName={() => {
          return "grid-row";
        }}
        apiRef={apiRef}
        localeText={{
          noRowsLabel: "",
          footerTotalRows: t("model_list_total_rows") + " : ",
          footerRowSelected: (count) => t("list_total_selecedtitem") + " : " + count,
        }}
      />
    );
  }, [rows, i18n.language]);

  const dialog: CSSProperties | undefined = showEvaluationDialog
      ? undefined
      : { display: "none" };

  function createEvaluationDialog():JSX.Element {
    let contents = <div />
    contents = (
      <TrimmingChartDialog
        show={showEvaluationDialog}
        width={width}
        height={height}
        edit={false}
        cheight={height * 0.5}
        prot={selectProt}
        onCancel={handleCloseEvaluationDialog}
        modelevalinfo={dialogInfo}
      />
    );
    return contents;  
  }
  const evaluationDialog = createEvaluationDialog();

  //評価状況に合わせて説明文を切り替える
  function loadDescription(): string {
    let description = "";
    if (fkdppevalsts === StatusNone) {
      description = descNone;
    } else if (fkdppevalsts === StatusDoing) {
      description = descDoing;
    } else {
      description = descDone;
    }
    return description;
  }
  const description = loadDescription();

  //評価結果があるとき、ないときで表または説明文の表示を切り替える
  const display =
    fkdppevalsts === StatusDone && rows.length !== 0 ? (
      <div className="projectlist-grid">{dataGrid}</div>
    ) : (
      <div className="test-form">
        <h4>{description}</h4>
      </div>
    );

  //モデル学習時の設定によってボタンの有効、無効を切り替える
  const autoBtn = trainConfig === Automode ?
    ( <div>
        <Box sx ={{width: 200, p:1}}>
          <FormControlLabel value={Automode} control={<Radio />} label={label_auto} />
        </Box>
      </div> ) :
    ( <div>
        <Box sx ={{width: 200, p:1}}>
            <FormControlLabel disabled value={Automode} control={<Radio />} label={label_auto} />
        </Box>
      </div> );
  
    function createEvaluationMenu():JSX.Element {
    let contents = <div />;
    contents = (
      <FormControl fullWidth sx ={{m:1, width:1/3}}>
        <FormLabel id = "evaluationmode">{t("fkdpp_eval_mode")}</FormLabel>
          <RadioGroup 
            row
            aria-label="evaluationmode" name="mode"
            value={mode}
            onChange={handleChangeMode}
          >
            {autoBtn}
            <Box sx ={{width: 200, p:1}}><FormControlLabel value={Custommode} control={<Radio />} label={label_custom} /></Box>
          </RadioGroup>                 
      </FormControl>
    );
    return contents;
  }
  const evalutionMenu = createEvaluationMenu();

  function createCustomModeButton():JSX.Element {
    let contents = <div />;
    let filename = <div />;
    filename = <Box sx ={{width: 500, p:1}}>{t("name_configfile")}{evalconffilename}</Box>;
    contents = (
      <div className="contents">
        <div className="contents">{t("fkdpp_eval_custommodelabel")}</div>
          <div className="contents">
            <UploadSettingFileButton onChange={(e) => {handleSelectConfFile(e, evalconf);}}/>               
            <DownloadSFileButton onClick={() => handleDownloadConfFile(evalconf)}/>
            <ResetFileButton onClick={() => handleResetConfFile(evalconf)}/>   
            {filename}     
          </div>

      </div>
    );
    return contents;
  }
  const customModeButton = (mode === Custommode && uploading === false) 
    ? createCustomModeButton() : "";

  function createEvaluationButton():JSX.Element {
    let contents = <div />;
    contents = (
      <Button 
        id="evaluation"
        className="btn-mainspace" 
        onClick={() => handleEvaluationModel(mode)}>
        {t("fkdpp_eval_start")}
      </Button>
    );
    return contents;
  }
  const evaluationButton =  uploading === false ? createEvaluationButton() : "";

  return (
    <React.Fragment>
      <div className="mainspace">
        <div className="mainspace-title">
          <h3>{t("project_detail_project_name")}</h3>
          <h3 style={{ marginLeft: "10px" }}>{projectInfo.name}</h3>
        </div>
        <div className="mainspace-toolbar">
          <BackButton onClick={onBack} />
          <ReloadButton onClick={handleReload} /> 
          {evaluationButton}        
        </div>
        <div className="mainspace-toolbar">
          {evalutionMenu}
          </div>
          <div className="mainspace-toolbar">
          {customModeButton}          
        </div>
        <div className="mainspace-toolbar">
          <h3>{t("model_evaluation")}</h3>
        </div>
        
        {display}
        <div className="overlay" style={dialog}>{evaluationDialog}</div>

      </div>
    </React.Fragment>
  );
}
